import React, { useState } from "react";
import { ModalV2, Button, Text, Flex, toast, Banner, Card } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useCustomFields } from "../../hooks/useCustomFields";
import { CustomField, getFieldTypeLabel } from "../../types";
import * as styles from "../styles_vanilla.css";

interface Props {
  show: boolean;
  field: CustomField | null;
  onHide: () => void;
}

const DeleteCustomFieldModal: React.FC<Props> = ({ show, field, onHide }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { deleteField } = useCustomFields();

  const handleDelete = async () => {
    if (!field) return;

    try {
      setIsSubmitting(true);

      await deleteField(field.id);

      toast.success("Extension field deleted successfully");
      onHide();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete extension field";
      toast.error(errorMessage);
      // TODO: Implement proper error logging
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onHide();
  };

  if (!field) return null;

  return (
    <ModalV2 show={show} onHide={handleClose} title="Delete Custom Field">
      <ModalV2.Body>
        <Flex flexDirection="column" gap="16px">
          <Flex alignItems="center" gap="12px">
            <Icon type="ruler-triangle" size="large" />
            <Text variant="regular-xl">Are you sure you want to delete this field?</Text>
          </Flex>

          <Card className={styles.fieldInfoCard}>
            <Flex flexDirection="column" gap="8px">
              <Flex flexDirection="column" gap="2px">
                <Text variant="strong-sm" color="neutral-600" className={styles.fieldLabel}>
                  Field Name
                </Text>
                <Text variant="regular-lg" color="neutral-900">
                  {field.name}
                </Text>
              </Flex>
              <Flex flexDirection="column" gap="2px">
                <Text variant="strong-sm" color="neutral-600" className={styles.fieldLabel}>
                  Type
                </Text>
                <Text variant="regular-lg" color="neutral-900">
                  {getFieldTypeLabel(field.type)}
                </Text>
              </Flex>
            </Flex>
          </Card>

          <Banner
            variant="warning"
            title="Deletion Dependencies"
            description="This deletion operation will fail if the extension field has any existing associations or dependencies. Please ensure the field is not in use before attempting to delete it."
          />
        </Flex>
      </ModalV2.Body>

      <ModalV2.Footer>
        <div className={styles.modalFooter}>
          <Button variant="neutral" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            data-testid="confirm-delete-button"
          >
            Delete Field
          </Button>
        </div>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default DeleteCustomFieldModal;
